import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import get from 'lodash.get';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import utc from 'dayjs/plugin/utc';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import timezone from 'dayjs/plugin/timezone';

import { appActions, rtApiListActions } from 'src/store/actions';
import { Page } from 'src/containers';
import { Col, Div, Icon, Row, Text } from '@pinpoint/design-kit';
import { Button, GridWrapper, StatusFilterButtonGroup } from 'src/components';
import { CellActions, CellDate, CellName, CellStatus, CellTag, CellText } from 'src/components/realtime_api/cells';
import CONSTANTS from 'src/store/constants';
import { ROUTES } from 'src/config';
import { noOp, valFromQs } from 'src/functions';
import usePlatformConfig from 'src/hooks/usePlatformConfig';
import { CreateRealtimeConfigDialog, ShowRtApiKeyModal } from 'src/components/dialogs';
import { ModelProp, RtCsProp } from 'src/store/typings';
import { CellRegulatoryTag } from 'src/components/models/cells';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(advancedFormat);
dayjs.extend(customParseFormat);

/* eslint-disable no-console */
const typeColW = '2fr';
const smallTagW = '1fr';

function makeColWidths(hasFramework) {
  return `2rem 3fr 3fr 3fr ${typeColW} repeat(${hasFramework ? 5 : 4}, ${smallTagW})`;
}

function RtApiList({
  history,
  app: {
    profile,
    profile: { role, siteRole, tenant },
  },
  enqueueNotification,
  approveConfig,
  createConfig,
  deleteConfig,
  editConfig,
  fetchConfigList,
  fetchModels,
  setGridState,
  deselectAllConfigIds,
  selectGridRow,
  deselectGridRow,
  fetchApiKey,
  handleUnmount,
  rtApiList: {
    apiKey,
    fetchApiKeyStatus,
    fetchApiKeyError,
    fetchConfigListStatus,
    fetchConfigListError,
    configList,
    approveConfigStatus,
    approveConfigError,
    createConfigStatus,
    createConfigError,
    deleteConfigStatus,
    deleteConfigError,
    editConfigStatus,
    editConfigError,
    gridState,
    controlledGridState,
    selectedConfigIds,
    models,
  },
}) {
  const search = new URLSearchParams(history.location.search);
  const [apiKeyModalOpen, setApiKeyModalOpen] = useState(false);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [selectedCode, setSelectedCode] = useState(valFromQs(search, 'filter', CONSTANTS.REAL_TIME_API.CS_FILTERS, ''));

  const platformConfig = usePlatformConfig();

  const isDeprecatedPlatform = useMemo(() => {
    const config = configList.find((cs) => cs.id === selectedConfigIds[0]);
    return config
      ? !platformConfig.canAction(config?.config_metadata?.model?.modelData?.metadataPayload?.refresh?.platform)
      : false;
  }, [configList, platformConfig, selectedConfigIds]);
  const demoDisabled = useMemo(
    () =>
      !selectedConfigIds ||
      selectedConfigIds.length === 0 ||
      get(
        configList.find((cs) => cs.id === selectedConfigIds[0]),
        'recentStatus',
        get(
          configList.find((cs) => cs.id === selectedConfigIds[0]),
          'recent_status'
        )
      ) !== CONSTANTS.REAL_TIME_API.CS_STATE.CONFIG_GENERATION_SUCCESS ||
      isDeprecatedPlatform,
    [selectedConfigIds, configList, isDeprecatedPlatform]
  );
  const demoTooltip = useMemo(() => {
    if (demoDisabled && isDeprecatedPlatform) {
      return 'The selected config uses a deprecated platform';
    }
    if (demoDisabled) {
      return 'Select a completed API';
    }
    return 'Test inputs and outputs';
  }, [demoDisabled, isDeprecatedPlatform]);

  const usageDisabled = useMemo(
    () =>
      !selectedConfigIds ||
      selectedConfigIds.length === 0 ||
      get(
        configList.find((cs) => cs.id === selectedConfigIds[0]),
        'recentStatus',
        get(
          configList.find((cs) => cs.id === selectedConfigIds[0]),
          'recent_status'
        )
      ) !== CONSTANTS.REAL_TIME_API.CS_STATE.CONFIG_GENERATION_SUCCESS,
    [configList, selectedConfigIds]
  );
  const isAdmin = useMemo(
    () => role === CONSTANTS.ROLE.ADMIN || siteRole === CONSTANTS.ROLE.SUPER_ADMIN,
    [role, siteRole]
  );
  const configData = useMemo(
    () => configList.map((config) => ({ ...config, selectedCode })),
    [configList, selectedCode]
  );
  const getStatusGroup = useCallback(
    (status) => CONSTANTS.REAL_TIME_API.CS_STATE_GROUPINGS[status] || 0 /* unknown status */,
    []
  );
  const tableDataReady = useMemo(() => configData && configData.length > 0, [configData]);

  const hasFramework = useMemo(
    () => configData.some(({ config_metadata: meta }) => meta?.model?.framework?.id),
    [configData]
  );

  const configCols = useMemo(() => {
    const cols = [
      {
        id: 'name',
        Header: <Text textSize="small">Name</Text>,
        justify: 'left',
        cellJustify: 'left',
        Cell: ({ value, row }) =>
          CellName({
            value,
            row,
            getDeprecatedTag: platformConfig.getDeprecatedTag,
            'data-tip': value,
            textWeight: '400',
            textSize: 'small',
            maxW: '10rem',
            align: 'center',
            m: { r: '0.5rem' },
            style: { textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' },
          }),
        accessor: ({ config_metadata: meta }) => meta.model.name,
      },
      {
        id: 'description',
        Header: <Text textSize="small">Description</Text>,
        justify: 'left',
        cellJustify: 'left',
        Cell: ({ value }) =>
          CellText({
            value,
            'data-tip': value,
            textWeight: '400',
            textSize: 'small',
            maxW: '10rem',
            align: 'center',
            m: { r: '0.5rem' },
            style: { textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' },
          }),
        accessor: ({ description }) => description,
        isEditable: isAdmin ? CONSTANTS.CONTROL_STATES.ENABLED : CONSTANTS.CONTROL_STATES.DISABLED,
        onEdit: (config, description) => {
          editConfig(config.id, { description });
        },
      },
      {
        id: 'prediction',
        Header: <Text textSize="small">Prediction</Text>,
        justify: 'left',
        cellJustify: 'left',
        Cell: ({ value }) =>
          CellText({
            value,
            'data-tip': value,
            textWeight: '400',
            textSize: 'small',
            maxW: '10rem',
            align: 'center',
            m: { r: '0.5rem' },
            style: { textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' },
          }),
        accessor: (cs) => {
          const predictionType = get(cs, 'config_metadata.model.predictionType');
          if (['', null, undefined].includes(predictionType)) return get(cs, 'config_metadata.model.typeDetail', '-');
          return predictionType;
        },
      },
      {
        id: 'score_type',
        Header: <Text textSize="small">Type</Text>,
        justify: 'left',
        cellJustify: 'left',
        accessor: ({ config_metadata: meta }) => ({
          dataModelType: meta?.model?.dataModelType || meta?.data_model_type,
          typeDetail: meta?.model?.typeDetail || meta?.type_detail,
          predictionType: meta?.model?.predictionType || meta?.prediction_type,
          isRemediated:
            meta?.model?.modelData?.metadataPayload?.fairness || meta?.model_data?.metadata_payload?.fairness,
        }),
        Cell: CellTag,
      },
      {
        Header: <Text textSize="small">Created</Text>,
        id: 'created',
        cellJustify: 'center',
        accessor: (entity) => dayjs.utc(get(entity, 'created_at', get(entity, 'createdAt', '1970-01-01'))).toDate(),
        sortType: 'datetime',
        Cell: CellDate,
      },
      {
        Header: <Text textSize="small">Actions</Text>,
        id: 'actions',
        cellJustify: 'center',
        disableSortBy: true,
        accessor: ({ id }) => id,
        Cell: ({ value, row }) => {
          const { index: idx } = row;
          const { platform } = row?.original?.config_metadata?.model?.refresh ?? {};
          return CellActions({
            value,
            approveConfig,
            deleteConfig,
            status: get(configList, `[${idx}].recent_status`, get(configList, `[${idx}].recentStatus`, '')),
            enableApproveConfig: platformConfig.canAction(platform),
          });
        },
      },
      {
        id: 'csId',
        Header: <Text textSize="small">Config Setting ID</Text>,
        justify: 'left',
        cellJustify: 'left',
        Cell: ({ value }) =>
          CellText({
            value: `${value || ''}`,
            'data-tip': value,
            textWeight: '400',
            textSize: 'small',
            maxW: '10rem',
            align: 'center',
            m: { r: '0.5rem' },
            style: { textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' },
          }),
        accessor: ({ id, recentStatus }) =>
          recentStatus === CONSTANTS.REAL_TIME_API.CS_STATE.CONFIG_GENERATION_SUCCESS ? id : null,
      },
      {
        id: 'recent_status',
        Header: <Text textSize="small">Status</Text>,
        justify: 'center',
        cellJustify: 'center',
        Cell: CellStatus,
        accessor: (data) => getStatusGroup(data.recentStatus || data.recent_status),
      },
    ];
    if (hasFramework)
      // @ts-ignore
      cols.splice(-4, 0, {
        id: 'regulatory',
        Header: (
          <Text textSize="small" m={{ p: '1rem' }}>
            Regulatory
          </Text>
        ),
        disableSortBy: true,
        justify: 'left',
        cellJustify: 'left',
        // @ts-ignore
        accessor: ({ config_metadata: meta }) => ({ framework: meta?.model?.framework || null }),
        // @ts-ignore
        Cell: (value) => CellRegulatoryTag({ ...value, bg: 'white' }),
      });
    return cols;
  }, [isAdmin, hasFramework, platformConfig, editConfig, approveConfig, deleteConfig, configList, getStatusGroup]);

  const colWidths = useMemo(() => makeColWidths(hasFramework), [hasFramework]);

  const openApiKeyModal = useCallback(() => {
    setApiKeyModalOpen(true);
  }, [setApiKeyModalOpen]);
  const closeApiKeyModal = useCallback(() => {
    setApiKeyModalOpen(false);
  }, [setApiKeyModalOpen]);
  const openCreateModal = useCallback(() => setCreateModalOpen(true), [setCreateModalOpen]);
  const closeCreateModal = useCallback(() => setCreateModalOpen(false), [setCreateModalOpen]);
  const getData = useCallback(() => {
    const filter = {
      filters: [
        {
          name: 'score_type',
          op: 'in',
          val: ['static', 'model', 'enrichment'],
        },
      ],
      order_by: [{ field: 'created_at', direction: 'desc' }],
      includeRefresh: platformConfig.includeRefresh,
    };

    switch (selectedCode) {
      case CONSTANTS.REAL_TIME_API.CS_FILTER.PENDING_APPROVAL: {
        filter.filters.push({
          name: 'recent_status',
          op: 'in',
          val: [CONSTANTS.REAL_TIME_API.CS_STATE.PENDING_APPROVAL],
        });
        break;
      }
      case CONSTANTS.REAL_TIME_API.CS_FILTER.IN_PROGRESS: {
        filter.filters.push({
          name: 'recent_status',
          op: 'in',
          val: [
            CONSTANTS.REAL_TIME_API.CS_STATE.APPROVED,
            CONSTANTS.REAL_TIME_API.CS_STATE.CONFIG_GENERATION_INITIATED,
          ],
        });
        break;
      }
      case CONSTANTS.REAL_TIME_API.CS_FILTER.ERRORED: {
        filter.filters.push({
          name: 'recent_status',
          op: 'in',
          val: [
            CONSTANTS.REAL_TIME_API.CS_STATE.CONFIG_GENERATION_FAILURE,
            CONSTANTS.REAL_TIME_API.CS_STATE.CONFIG_WARM_UP_FAILURE,
          ],
        });
        break;
      }
      case CONSTANTS.REAL_TIME_API.CS_FILTER.COMPLETED: {
        filter.filters.push({
          name: 'recent_status',
          op: 'in',
          val: [CONSTANTS.REAL_TIME_API.CS_STATE.CONFIG_GENERATION_SUCCESS],
        });
        break;
      }
      default:
        filter.filters.push({
          name: 'recent_status',
          op: 'in',
          val: CONSTANTS.REAL_TIME_API.CS_STATES,
        });
        break;
    }

    try {
      fetchConfigList(filter);
    } catch (err) {
      console.error(err);
    }
  }, [selectedCode, fetchConfigList, platformConfig.includeRefresh]);

  const handleFilterButtonClick = useCallback(
    (filterType) => {
      if (selectedCode === filterType) return setSelectedCode('');
      return setSelectedCode(filterType);
    },
    [selectedCode]
  );

  useEffect(() => {
    if (tenant) {
      fetchModels({
        filters: [
          {
            name: 'recent_status',
            op: 'in',
            val: [
              CONSTANTS.TAL_MODEL.STATE.LIFT_CHART_GENERATION_SUCCESS,
              CONSTANTS.TAL_MODEL.STATE.MODEL_REMEDIATION_PENDING,
              CONSTANTS.TAL_MODEL.STATE.MODEL_REMEDIATION_SETUP,
              CONSTANTS.TAL_MODEL.STATE.MODEL_REMEDIATION_SETUP_SUCCESS,
              CONSTANTS.TAL_MODEL.STATE.MODEL_REMEDIATION_SUCCESS,
            ],
          },
          {
            name: 'data_model_type',
            op: 'in',
            val: [
              CONSTANTS.TAL_MODEL.TAG.ACT_FREQUENCY,
              CONSTANTS.TAL_MODEL.TAG.ACT_LOSS_DIRECT,
              CONSTANTS.TAL_MODEL.TAG.ACT_LOSS_MULT,
              CONSTANTS.TAL_MODEL.TAG.ACT_SEVERITY,
              CONSTANTS.TAL_MODEL.TAG.CLASSIFIER,
            ],
          },
        ],
        order_by: [{ field: 'created_at', direction: 'desc' }],
        fields: 'items{name,id,dataModelType,hiddenByRole,framework,modelData,typeDetail,predictionType,trainingType}',
        includeRefresh: platformConfig.includeRefresh,
      });
    }
  }, [tenant, fetchModels, platformConfig.includeRefresh]);

  useEffect(() => {
    if (tenant) fetchApiKey();
  }, [tenant, fetchApiKey]);

  useEffect(() => {
    if (tenant) {
      const hasState = ![null, undefined].includes(selectedCode);
      const query = `?filter=${selectedCode}`;
      if (hasState) {
        getData();
        history.replace({
          pathname: '/rt',
          search: query,
          state: {},
        });
      }
    }
    return () => {
      handleUnmount();
    };
  }, [tenant, selectedCode, history, getData, handleUnmount]);

  useEffect(() => {
    if (createConfigStatus === CONSTANTS.API.FAILED) {
      enqueueNotification(createConfigError);
    } else if (createConfigStatus === CONSTANTS.API.SUCCEEDED) {
      enqueueNotification('New config creation awaiting Pinpoint approval', { autoClose: true, timeout: 4000 });
      // allows backend generation events to finish before loading
      setTimeout(() => getData(), 1000);
    }
  }, [getData, createConfigStatus, createConfigError, enqueueNotification]);

  useEffect(() => {
    if (approveConfigStatus === CONSTANTS.API.FAILED) {
      enqueueNotification(approveConfigError);
    } else if (approveConfigStatus === CONSTANTS.API.SUCCEEDED) {
      enqueueNotification('Config approved', { autoClose: true, timeout: 4000 });
      // allows backend generation events to finish before loading
      setTimeout(() => getData(), 1000);
    }
  }, [getData, approveConfigStatus, approveConfigError, enqueueNotification]);

  useEffect(() => {
    if (editConfigStatus === CONSTANTS.API.FAILED) {
      enqueueNotification(editConfigError);
    }
  }, [editConfigStatus, editConfigError, enqueueNotification]);

  useEffect(() => {
    if (deleteConfigStatus === CONSTANTS.API.SUCCEEDED) {
      // allows backend generation events to finish before loading
      setTimeout(() => getData(), 1000);
    } else if (deleteConfigStatus === CONSTANTS.API.FAILED) {
      enqueueNotification(deleteConfigError);
    }
  }, [getData, deleteConfigStatus, deleteConfigError, enqueueNotification]);

  useEffect(() => {
    if (fetchConfigListStatus === CONSTANTS.API.FAILED) {
      enqueueNotification(fetchConfigListError);
    }
    if (fetchApiKeyStatus === CONSTANTS.API.FAILED) {
      enqueueNotification(fetchApiKeyError);
    }
  }, [fetchApiKeyStatus, fetchApiKeyError, fetchConfigListStatus, fetchConfigListError, enqueueNotification]);

  return (
    <Div flexDir="column" d="flex" h="100%" minH="100%" w="100%" minW="100%" data-debug="content" p={{ x: '-1.15rem' }}>
      <Row data-debug="nav-row" m={{ x: '1.5rem', t: '1.5rem', b: '.9rem' }}>
        <Col justify="left" align="center" gutterWidth="0" d="flex" size="6">
          <Button
            buttonStyle="primary"
            data-tip="View documentation for your IT team"
            onClick={() => {
              history.push(
                selectedConfigIds && selectedConfigIds.length > 0
                  ? ROUTES.REAL_TIME_API_DOCUMENTATION_CONFIG.replace(':configId', selectedConfigIds[0])
                  : ROUTES.REAL_TIME_API_DOCUMENTATION
              );
              deselectAllConfigIds();
            }}
          >
            <Icon name="ApiDocs" color="white" m={{ r: '0.5rem' }} viewBox="0 0 15 18" size="1rem" />
            DOCUMENTATION
          </Button>
          <Button buttonStyle="primary" data-tip="Client-level key" onClick={openApiKeyModal}>
            <Icon name="Key" color="white" m={{ r: '0.5rem' }} size="1.5rem" viewBox="0 0 21 10" />
            API KEY
          </Button>
          <Button buttonStyle="primary" data-tip="Create a new real-time API" onClick={openCreateModal}>
            <Icon name="AddBoxed" color="white" m={{ r: '0.5rem' }} size="1rem" viewBox="0 0 14 14" />
            NEW API
          </Button>
        </Col>
        <Col flexGrow="1" d="flex" justify="right" align="center" gutterWidth="0">
          <Div data-test="demo-btn-div">
            <Button
              buttonStyle="primary"
              data-tip={demoTooltip}
              disabled={demoDisabled}
              onClick={() => {
                history.push(ROUTES.REAL_TIME_API_DEMO.replace(':configId', selectedConfigIds[0]));
                handleUnmount();
              }}
            >
              <Icon name="PresentationScreen" size="1rem" viewBox="0 0 18 16" color="white" m={{ r: '.5em' }} />
              <Text textSize="body">DEMO</Text>
            </Button>
          </Div>
          <Div data-test="usage-btn-div">
            <Button
              buttonStyle="primary"
              data-tip={usageDisabled ? 'Select a completed API' : 'View usage stats for selected API'}
              disabled={usageDisabled}
              onClick={() => {
                history.push(ROUTES.REAL_TIME_API_USAGE_CONFIG.replace(':configId', selectedConfigIds[0]));
                deselectAllConfigIds();
              }}
            >
              <Icon name="Usage" size="1rem" viewBox="0 0 17 17" color="white" m={{ r: '.5em' }} />
              <Text textSize="body">USAGE</Text>
            </Button>
          </Div>
          <Button
            buttonStyle="primary"
            m={{ r: '0rem' }}
            data-tip="View usage stats for all APIs"
            onClick={() => {
              history.push(ROUTES.REAL_TIME_API_USAGE_CONFIG.replace(':configId', ''));
            }}
          >
            <Icon name="TotalUsage" size="1rem" viewBox="0 0 16 16" color="white" m={{ r: '.5em' }} />
            <Text textSize="body">TOTAL USAGE</Text>
          </Button>
        </Col>
      </Row>

      <Row flexGrow="1" h="100%" data-debug="activation-row" m={{ x: '1.5rem', b: '.5rem' }}>
        <Col size="12" rounded="md" bg="white" gutterWidth="0">
          <Row data-debug="filter-row" align="center" m={{ t: '1rem', b: '.75rem' }} flexWrap="nowrap">
            <Col size="4" gutterWidth="1.5rem">
              <Text textSize="subtitle" textWeight="500" textColor="navyBlue" m={{ l: '2rem' }}>
                API Activation List
              </Text>
            </Col>
            <Col size="8" textAlign="right">
              <StatusFilterButtonGroup
                selectedCode={selectedCode}
                apiStatus={fetchConfigListStatus}
                onFilterButtonClick={handleFilterButtonClick}
              />
            </Col>
          </Row>
          <Row
            data-debug="table-row"
            m={{ t: '1rem', b: '.75rem' }}
            bg="white"
            rounded={{ bl: 'md', br: 'md' }}
            flexDir="row"
            align="center"
          >
            <Col size="12">
              <GridWrapper
                rawData={configList}
                apiStatus={fetchConfigListStatus}
                columns={configCols}
                data={configList}
                gridState={gridState}
                controlledGridState={controlledGridState}
                getData={getData}
                hasData={tableDataReady}
                DivProps={{ m: '0rem', p: '0rem' }}
                TableProps={{ p: { y: '0rem' } }}
                tenant={tenant}
                hasSelection
                onRowSelect={selectGridRow}
                setGridState={setGridState}
                onRowDeselect={deselectGridRow}
                hasExpansion={false}
                maxSelection={1}
                widths={colWidths}
              />
            </Col>
          </Row>
        </Col>
      </Row>
      <CreateRealtimeConfigDialog
        isOpen={createModalOpen}
        actions={{
          cancel: { on: noOp },
          close: { on: closeCreateModal },
          handlers: [{ on: createConfig, after: getData }],
        }}
        models={platformConfig.getModels(models)}
        profile={profile}
      />
      <ShowRtApiKeyModal
        isOpen={apiKeyModalOpen}
        apiKey={apiKey}
        actions={{
          cancel: { on: noOp },
          close: { on: closeApiKeyModal },
          handlers: [
            {
              on: navigator.clipboard.writeText,
            },
          ],
        }}
      />
    </Div>
  );
}

const mapStateToProps = ({ app, rtApiList }) => ({
  app,
  rtApiList,
});

const mapDispatchToProps = (dispatch) => ({
  approveConfig: (configId) => dispatch(rtApiListActions.approveConfig(configId)),
  createConfig: (...params) => dispatch(rtApiListActions.createConfig(...params)),
  deleteConfig: (configId) => dispatch(rtApiListActions.deleteConfig(configId)),
  editConfig: (...params) => dispatch(rtApiListActions.editConfig(...params)),
  fetchApiKey: (...params) => dispatch(rtApiListActions.fetchApiKey(...params)),
  fetchConfigList: (...params) => dispatch(rtApiListActions.fetchConfigList(...params)),
  fetchModels: (...params) => dispatch(rtApiListActions.fetchModels(...params)),
  enqueueNotification: (...params) => dispatch(appActions.enqueueNotification(...params)),
  setGridState: (state) => dispatch(rtApiListActions.setGridState(state)),
  selectGridRow: (row) => dispatch(rtApiListActions.selectRow(row)),
  deselectAllConfigIds: () => dispatch(rtApiListActions.deselectAllConfigIds()),
  deselectGridRow: (row) => dispatch(rtApiListActions.deselectRow(row)),
  handleUnmount: () => {
    dispatch(rtApiListActions.deselectAllConfigIds());
    dispatch(rtApiListActions.unset('approveConfigStatus'));
    dispatch(rtApiListActions.unset('approveConfigError'));
    dispatch(rtApiListActions.unset('createConfigStatus'));
    dispatch(rtApiListActions.unset('createConfigError'));
    dispatch(rtApiListActions.unset('deleteConfigStatus'));
    dispatch(rtApiListActions.unset('deleteConfigError'));
    dispatch(rtApiListActions.unset('editConfigStatus'));
    dispatch(rtApiListActions.unset('editConfigError'));
  },
});

RtApiList.propTypes = {
  app: PropTypes.shape({
    profile: PropTypes.shape({
      originalTenant: PropTypes.string.isRequired,
      tenant: PropTypes.string.isRequired,
      role: PropTypes.string.isRequired,
      siteRole: PropTypes.string.isRequired,
    }),
  }).isRequired,
  history: PropTypes.shape({
    push: PropTypes.func.isRequired,
    replace: PropTypes.func.isRequired,
    location: PropTypes.shape({ search: PropTypes.string }).isRequired,
  }).isRequired,
  selectGridRow: PropTypes.func.isRequired,
  setGridState: PropTypes.func.isRequired,
  deselectAllConfigIds: PropTypes.func.isRequired,
  deselectGridRow: PropTypes.func.isRequired,
  approveConfig: PropTypes.func.isRequired,
  createConfig: PropTypes.func.isRequired,
  deleteConfig: PropTypes.func.isRequired,
  editConfig: PropTypes.func.isRequired,
  fetchApiKey: PropTypes.func.isRequired,
  fetchConfigList: PropTypes.func.isRequired,
  fetchModels: PropTypes.func.isRequired,
  enqueueNotification: PropTypes.func.isRequired,
  handleUnmount: PropTypes.func.isRequired,
  rtApiList: PropTypes.shape({
    apiKey: PropTypes.string.isRequired,
    fetchApiKeyStatus: PropTypes.string,
    fetchApiKeyError: PropTypes.string,
    configList: PropTypes.arrayOf(RtCsProp).isRequired,
    fetchConfigListStatus: PropTypes.string,
    fetchConfigListError: PropTypes.string,
    approveConfigStatus: PropTypes.string,
    approveConfigError: PropTypes.string,
    createConfigStatus: PropTypes.string,
    createConfigError: PropTypes.string,
    deleteConfigStatus: PropTypes.string,
    deleteConfigError: PropTypes.string,
    editConfigStatus: PropTypes.string,
    editConfigError: PropTypes.string,
    rtApiKey: PropTypes.string.isRequired,
    controlledGridState: PropTypes.shape({}).isRequired,
    gridState: PropTypes.shape({}).isRequired,
    maxGridRowSelection: PropTypes.number.isRequired,
    selectedConfigIds: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    models: PropTypes.arrayOf(ModelProp).isRequired,
  }).isRequired,
};

export default connect(mapStateToProps, mapDispatchToProps)(Page(RtApiList));
